/**
 * Shares one request between callers that ask for the same key around the same
 * time (e.g. a page, its sidebar and the panel it opens all loading the agent
 * list during one page load).
 *
 * By default it is NOT a cache: the entry is dropped as soon as the request
 * settles, so the next call hits the network. Pass `reuseForMs` to also hand a
 * successful result to callers that arrive within that window after it landed —
 * keep it short (seconds), so data the user just changed is never served stale
 * for long. Failures are never reused.
 */
type Entry = { promise: Promise<unknown>; expiresAt: number };
const entries = new Map<string, Entry>();

export function shareInFlight<T>(key: string, request: () => Promise<T>, reuseForMs = 0): Promise<T> {
  const existing = entries.get(key);
  if (existing && existing.expiresAt > Date.now()) return existing.promise as Promise<T>;

  const entry: Entry = { promise: Promise.resolve(), expiresAt: Number.POSITIVE_INFINITY };
  entry.promise = request().then(
    (value) => {
      if (entries.get(key) === entry) {
        if (reuseForMs > 0) entry.expiresAt = Date.now() + reuseForMs;
        else entries.delete(key);
      }
      return value;
    },
    (err) => {
      if (entries.get(key) === entry) entries.delete(key);
      throw err;
    },
  );
  entries.set(key, entry);
  return entry.promise as Promise<T>;
}

/** Drops shared/reused results whose key starts with `prefix` — call after a write. */
export function forgetShared(prefix: string): void {
  entries.forEach((_entry, key) => {
    if (key.startsWith(prefix)) entries.delete(key);
  });
}

/** Return an in-flight or cached promise for the given key, if one exists. */
export function getInFlight<T>(key: string): Promise<T> | null {
  const existing = entries.get(key);
  if (existing && existing.expiresAt > Date.now()) return existing.promise as Promise<T>;
  return null;
}
